import { useEffect, useId, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { MAJOR_KEY_OPTIONS, MINOR_KEY_OPTIONS } from "../../../shared/chords";
import type { Arrangement, Person, PlanDetail, SongDetail, SongSummary } from "../../../shared/types";
import { Dialog, ErrorNote } from "../../ui";
import { addItem, loadSong, searchSongs } from "./client";
import { messageOf, parseDuration } from "./model";

export function SongPicker({ planId, people, onClose, onPlan }: {
  planId: string;
  people: Person[];
  onClose: () => void;
  onPlan: (plan: PlanDetail) => void;
}) {
  const searchId = useId();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SongSummary[]>([]);
  const [detail, setDetail] = useState<SongDetail | null>(null);
  const [arrangementId, setArrangementId] = useState("");
  const [keyName, setKeyName] = useState("");
  const [minutes, setMinutes] = useState("4");
  const [seconds, setSeconds] = useState("0");
  const [leaderId, setLeaderId] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    const handle = window.setTimeout(() => {
      void searchSongs(query)
        .then((rows) => { if (!cancel) setResults(rows); })
        .catch((caught: unknown) => { if (!cancel) setError(messageOf(caught)); });
    }, 180);
    return () => { cancel = true; window.clearTimeout(handle); };
  }, [query]);

  function choose(song: SongSummary) {
    setError("");
    setDetail(null);
    void loadSong(song.id)
      .then((row) => {
        const arrangement = row.arrangements.find((item) => item.isDefault) ?? row.arrangements[0];
        setDetail(row);
        setArrangementId(arrangement?.id ?? "");
        setKeyName(arrangement?.keyName ?? row.defaultKey);
      })
      .catch((caught: unknown) => setError(messageOf(caught)));
  }

  function selectArrangement(arrangement: Arrangement) {
    setArrangementId(arrangement.id);
    setKeyName(arrangement.keyName);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!detail) return;
    const durationSeconds = parseDuration(minutes, seconds);
    if (durationSeconds === null) {
      setError("Use a duration up to 8 hours.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      onPlan(await addItem(planId, {
        kind: "song",
        songId: detail.id,
        arrangementId: arrangementId || null,
        keyName: keyName || null,
        durationSeconds,
        leaderPersonId: leaderId || null,
        description,
      }));
      onClose();
    } catch (caught) {
      setError(messageOf(caught));
      setBusy(false);
    }
  }

  const major = keyName && !keyName.endsWith("m") && !MAJOR_KEY_OPTIONS.includes(keyName) ? [keyName, ...MAJOR_KEY_OPTIONS] : [...MAJOR_KEY_OPTIONS];
  const minor = keyName.endsWith("m") && !MINOR_KEY_OPTIONS.includes(keyName) ? [keyName, ...MINOR_KEY_OPTIONS] : [...MINOR_KEY_OPTIONS];

  return (
    <Dialog title="Add a song" onClose={onClose}>
      <form className="form-grid" onSubmit={(event) => void submit(event)}>
        {error && <ErrorNote>{error}</ErrorNote>}
        {!detail && (
          <>
            <div className="field">
              <label htmlFor={searchId}>Search the library</label>
              <input id={searchId} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Title, author, or tag" autoFocus />
            </div>
            <div className="stack svc-results">
              {results.map((song) => (
                <button className="btn" type="button" key={song.id} onClick={() => choose(song)}>
                  {song.title}<span className="faint"> · {song.defaultKey}{song.authors ? ` · ${song.authors}` : ""}</span>
                </button>
              ))}
              {results.length === 0 && <p className="muted">No songs match. <Link to="/songs">Open the song library</Link> to write one, then come back.</p>}
            </div>
          </>
        )}
        {detail && (
          <>
            <div className="split">
              <strong>{detail.title}</strong>
              <button className="btn small ghost" type="button" onClick={() => setDetail(null)}>Choose another</button>
            </div>
            <fieldset>
              <legend>Arrangement</legend>
              {detail.arrangements.length === 0 && <p className="muted">This song has no arrangement yet. It can still be added in {detail.defaultKey}.</p>}
              {detail.arrangements.map((arrangement) => (
                <label className={arrangementId === arrangement.id ? "svc-type on" : "svc-type"} key={arrangement.id}>
                  <input type="radio" name="arrangement" checked={arrangementId === arrangement.id} onChange={() => selectArrangement(arrangement)} />
                  <span className="svc-type-bar" style={{ background: "#8b6cff" }} />
                  <span>
                    <strong>{arrangement.name}</strong>
                    <small className="faint">{arrangement.keyName} · {arrangement.meter}{arrangement.bpm ? ` · ${arrangement.bpm} bpm` : ""}{arrangement.isDefault ? " · default" : ""}</small>
                  </span>
                </label>
              ))}
            </fieldset>
            <KeyField id="plan-key" value={keyName} major={major} minor={minor} onChange={setKeyName} />
            <DurationFields minutes={minutes} seconds={seconds} onMinutes={setMinutes} onSeconds={setSeconds} />
            <LeaderField id="song-leader" people={people} value={leaderId} onChange={setLeaderId} />
            <div className="field"><label htmlFor="song-note">Note</label><textarea id="song-note" maxLength={4000} value={description} onChange={(event) => setDescription(event.target.value)} /></div>
            <p className="faint">It lands at the end of the order. Move it after it is saved. The stand opens this plan key.</p>
            <button className="btn primary" type="submit" disabled={busy}>{busy ? "Adding…" : "Add to the plan"}</button>
          </>
        )}
      </form>
    </Dialog>
  );
}

function KeyField({ id, value, major, minor, onChange }: {
  id: string;
  value: string;
  major: string[];
  minor: string[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>Key on this plan</label>
      <select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">No key</option>
        <optgroup label="Major">{major.map((key) => <option key={key} value={key}>{key}</option>)}</optgroup>
        <optgroup label="Minor">{minor.map((key) => <option key={key} value={key}>{key}</option>)}</optgroup>
      </select>
    </div>
  );
}

function DurationFields({ minutes, seconds, onMinutes, onSeconds }: {
  minutes: string;
  seconds: string;
  onMinutes: (value: string) => void;
  onSeconds: (value: string) => void;
}) {
  return (
    <div className="svc-editor-grid">
      <div className="field"><label htmlFor="song-minutes">Minutes</label><input id="song-minutes" type="number" min={0} max={480} value={minutes} onChange={(event) => onMinutes(event.target.value)} /></div>
      <div className="field"><label htmlFor="song-seconds">Seconds</label><input id="song-seconds" type="number" min={0} max={59} value={seconds} onChange={(event) => onSeconds(event.target.value)} /></div>
    </div>
  );
}

function LeaderField({ id, people, value, onChange }: {
  id: string;
  people: Person[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>Item leader</label>
      <select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">No leader</option>
        {people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
      </select>
    </div>
  );
}
